import React, { PureComponent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { branch } from "baobab-react/higher-order";
import autoBind from "react-autobind";
import ReactPlayer from "react-player";
import ReactAudioPlayer from "react-audio-player";
import DriveContext from "../../driveContext";

import { generateUUID, getFileTypeIcon, validateUrl } from "../../../../utils/utils";
import { copyToClipboard } from "../../../../utils/clipboard";
import { getUrlForExternalResource } from "../../../../actions/drive";
import { getMediaType } from "../../../../utils/mediaUtils";
import { showMediaPreviewModal } from "../../../../actions/actions";

import styles from "./tileNode.module.css";

const MAX_VISIBLE_LABELS = 6;

class TileNode extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			showAllNodeLabels: false
		};
		autoBind(this);
	}

	handleClick(event) {
		if (event.detail === 2) {
			this.handleDblClick();
		} else {
			this.handleSelect();
		}
	}

	handleSelect() {
		if (this.props.onSelect) {
			this.props.onSelect(this.props.node);
		}
	}

	handleDblClick() {
		if (this.props.onDblClick) {
			this.props.onDblClick(this.props.node);
		}
	}

	renderHeader() {

		const { node } = this.props;
		const description = this.getNodeMetadataDescription(node);

		let icon = (node.type === "folder") ? "folder" : getFileTypeIcon(node.ext);
		let iconComponent = <FontAwesomeIcon icon={icon} />;

		return (
			<div className={styles["header-container"] + " name"} onClick={() => this.handleCopy()}>
				<div className={styles["header-main"]}>
					<div className={styles["icon-container"]}>{iconComponent}</div>
					<div className={styles["header"]} title={node.path}>{node.name}</div>
				</div>
				{description &&
					<div className={styles["description-chip"]} title={description}>
						Description
					</div>
				}
			</div>
		);
	}

	renderFilePreview() {
		const { node, iconFontSize } = this.props;
		const { guid, id, type } = node;
		const key = `node-${guid ? guid : id ? id : generateUUID()}`;
		const metadataTitle = this.getNodeMetadataTitle(node);

		if (type !== "file") {
			return null;
		}

		const url = getUrlForExternalResource(node);
		const mediaType = getMediaType(node.ext);
		let preview = null;
		switch (mediaType) {
			case "video": {
				preview = url ? <ReactPlayer url={url} style={{ width: "100%", maxWidth: "400px" }} width="100%" height="100%" playing={false} playsInline={true} muted={false} loop controls /> : null;
				break;
			}
			case "audio":
			case "labelAudioFile":
			case "label1AudioFile":
			case "label2AudioFile":
			case "commentsAudioFile": {
				preview = url ? <ReactAudioPlayer style={{ width: "100%", maxWidth: "400px" }} src={url} controls /> : null;
				break;
			}
			case "image": {
				preview = url && validateUrl(url) ? <img alt="Preview" style={{ width: "100%", maxWidth: "400px" }} src={url} /> : null;
				break;
			}
			default: {
				preview = <FontAwesomeIcon style={{ fontSize: iconFontSize ? iconFontSize : "10vw" }} icon={getFileTypeIcon(node.ext, node)} />;
			}
		}

		return <div key={key} className={styles["file-preview-container"]}>
			<div className={styles["thumb-wrapper"]}>
				<div className="thumb">
					{preview}
				</div>
				{metadataTitle &&
					<div className={styles["title-overlay"]} title={metadataTitle}>
						{metadataTitle}
					</div>
				}
			</div>
		</div>;
	}

	handleCopy = async () => {
		const url = getUrlForExternalResource(this.props.node);
		if (!url) {
			return;
		}
		await copyToClipboard(encodeURI(url));
	}

	normalizeLabelSource(source) {
		if (!source) {
			return [];
		}

		if (Array.isArray(source)) {
			return source
				.map((entry) => {
					if (typeof entry === "string") {
						return entry.trim();
					}
					if (entry && typeof entry === "object") {
						const value = entry.label || entry.name || entry.Name || entry.tag || entry.Tag;
						return typeof value === "string" ? value.trim() : "";
					}
					return "";
				})
				.filter(Boolean);
		}

		if (typeof source === "string") {
			return source
				.split(",")
				.map((entry) => entry.trim())
				.filter(Boolean);
		}

		if (source && typeof source === "object") {
			return Object.keys(source)
				.map((entry) => entry.trim())
				.filter(Boolean);
		}

		return [];
	}

	getNodeLabels(node) {
		const metaData = node?.metaData || {};

		const sources = [
			metaData.tags,
			node.labels,
			metaData.openAiTags,
			metaData.rekognitionTags,
			metaData.labels,
			node.tags
		];

		for (const source of sources) {
			const normalized = this.normalizeLabelSource(source);
			if (normalized.length > 0) {
				return [...new Set(normalized)];
			}
		}

		return [];
	}

	getNodeMetadataTitle(node) {
		const title = node?.metaData?.title;
		if (typeof title !== "string") {
			return "";
		}
		return title.trim();
	}

	getNodeMetadataDescription(node) {
		const description = node?.metaData?.description;
		if (typeof description !== "string") {
			return "";
		}
		return description.trim();
	}

	handleToggleLabels(event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		this.setState((prev) => ({
			showAllNodeLabels: !prev.showAllNodeLabels
		}));
	}

	renderLabels() {
		const { node, showAllLabels } = this.props;
		const { showAllNodeLabels } = this.state;
		const labels = this.getNodeLabels(node);
		if (!showAllLabels || labels.length === 0) {
			return null;
		}
		const labelClassName = node?.searchResult ? `${styles.labelTag} ${styles.labelTagSearch}` : styles.labelTag;
		const visibleLabels = showAllNodeLabels ? labels : labels.slice(0, MAX_VISIBLE_LABELS);
		const hiddenLabelCount = labels.length - visibleLabels.length;
		const canToggle = labels.length > MAX_VISIBLE_LABELS;

		return (
			<div className={styles.labels}>
				{visibleLabels.map((label, i) => (
					<span key={i} className={labelClassName}>
						{label}
					</span>
				))}
				{!showAllNodeLabels && hiddenLabelCount > 0 &&
					<button type="button" className={`${styles.labelTagMore} ${styles.labelToggleButton}`} onClick={this.handleToggleLabels}>
						+{hiddenLabelCount} more
					</button>
				}
				{showAllNodeLabels && canToggle &&
					<button type="button" className={`${styles.labelTagMore} ${styles.labelToggleButton}`} onClick={this.handleToggleLabels}>
						Show less
					</button>
				}
			</div>
		);
	}



	render() {
		const { node, meta, accept } = this.props;
		const { guid, id, type } = node;
		const { active } = meta ? meta : node;

		const key = `tile-node-${guid ? guid : id ? id : generateUUID()}`;

		// console.log("Render node", name, toggled, active);

		let fnsSelect = {};
		let acceptable = (type === "folder" || !accept || (accept && node && node.metaData && node.metaData.type === accept));
		if (acceptable) {
			fnsSelect = {
				onClick: this.handleClick
			};
		}

		let previewButton;
		let mediaType = getMediaType(node.ext);
		if (mediaType === "video" ||
			mediaType === "audio" ||
			mediaType === "pdf" ||
			mediaType === "threeD" ||
			mediaType === "image") {
			let url = getUrlForExternalResource(node);

			previewButton = <div className="open-large" onClick={() => { this.props.dispatch(showMediaPreviewModal, url); }} title="View Media"><FontAwesomeIcon icon="expand-wide" /></div>;
		}

		return <div key={key} className={acceptable ? active ? "tile-node selected" : "tile-node" : "tile-node disabled"} {...fnsSelect} id={`drive-node-${this.context?.driveComponentId}-${node.guid}`}>
			{this.renderHeader()}
			{node.type === "file" && this.renderFilePreview()}
			{node.type === "file" && this.renderLabels()}
			<div className="footer">
				{previewButton}
			</div>
		</div>;
	}
}

TileNode.contextType = DriveContext;

export default branch((props) => {
	// console.log("Mapping", props.node.guid, props.node);
	if (props.node.guid) {
		return {
			meta: ["drive", "hierarchyMetadata", props.node.guid]
		};
	} else {
		return {};
	}
}, TileNode);
